import React, { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader, OverlayTrigger, Tooltip } from "react-bootstrap";
import Select from "react-select";
import Button from "../../form-components/Button";
import Input from "../../form-components/Input";
import { updateTone } from "../../../features/tones/updateToneSlice";
import { createTone } from "../../../features/tones/createToneSlice";
import { getTone } from "../../../features/tones/getToneSlice";
import { showTone } from "../../../features/tones/showToneSlice";
import Loader from "../../../common/loader/Loader";
import { useDispatch, useSelector } from "react-redux";
import importAll from "../../chat-component/images";
import { wordCount } from "../../../utils/utils";

const images = importAll(
  require.context("../../../assets/images/", false, /\.(png|jpe?g|svg|GIF|webp)$/)
);

const NAME_LENGTH = 20, DESCRIPTION_LENGTH = 300;

export const generalAccessOptions = [
  {
    label: "Private",
    value: "private",
  },
  {
    label: "Public",
    value: "public",
  },
  // {
  //   label: "Shared with team",
  //   value: "Shared with team",
  // },
];

const emptyTone = {
  id: "",
  uuid: "",
  name: "",
  description: "",
  general_access: generalAccessOptions[0].value,
  is_default: false,
};

const ToneModal = ({
  isVisible,
  toggleFunction,
  tone,
  modalTitle,
}) => {
  const dispatch = useDispatch();
  const { responseData: toneData } = useSelector((state) => state?.showToneReducer);
  const { showIsLoading, editIsLoading, createIsLoading } = useSelector(
      (state) => ({
        showIsLoading: state?.showToneReducer?.isLoading,
        editIsLoading: state?.updateToneReducer?.isLoading,
        createIsLoading: state?.createToneReducer?.isLoading,
      })
  );
  const [input, setInput] = useState({ ...emptyTone });
  const [inputError, setInputError] = useState({});
  const [isNameFocused, setIsNameFocused] = useState(false);
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);

  const handleChangeInput = (key, value) => {
    input[key] = value;
    setInput({ ...input });
  }

  const save = async () => {
    let error = {};
    
    if (!input.name.trim()) {
      error.name = "Please enter name";
    }
    else if (input.name.length > NAME_LENGTH) {
      error.name = `Name should contain less than ${NAME_LENGTH} characters.`;
    }

    if (!input.description.trim()) {
      error.description = "Please enter description";
    }
    else if (input.description.length > DESCRIPTION_LENGTH) {
      error.description = `Description should contain less than ${DESCRIPTION_LENGTH} characters.`;
    }

    if (!input.general_access.trim()) {
      error.general_access = "Please enter general access";
    }

    setInputError({ ...error });

    if (Object.keys(error).length === 0) {
      input.is_default = input.is_default ? 1 : 0;
      if (input.uuid) {
        await dispatch(updateTone({ payload: input, uuid: input.uuid }));
      }
      else {
        await dispatch(createTone(input));
      }
      toggleFunction();
      await dispatch(getTone({ perPage: 10, currentPage: 1 }));
    }
  };

  useEffect(() => {
    setInput({ ...emptyTone });
    setInputError({});
    if (tone.uuid) {
      dispatch(showTone(tone.uuid));
    }
  }, [tone]);

  useEffect(() => {
    if (toneData) {
      setInput({
        id: toneData.id ?? emptyTone.id,
        uuid: toneData.uuid ?? emptyTone.uuid,
        name: toneData.name ?? emptyTone.name,
        description: toneData.description ?? emptyTone.description,
        general_access: toneData.general_access ?? emptyTone.general_access,
        is_default: toneData.is_default?.toString() === "1",
      });
    }
  }, [toneData]);

  return (
    <>
      {(showIsLoading || editIsLoading || createIsLoading) && <Loader />}
      <Modal
        data-bs-keyboard="false"
        onHide={toggleFunction}
        show={isVisible}
        className="modal fade addsummary-tone-modal addsummary-prompt-modal"
        centered={true}
        dialogClassName="common-modal"
      >
        <ModalHeader closeButton onHide={toggleFunction}>
          <h3 className="modal-title">{modalTitle}</h3>
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-md-12">
              <div className="form-group">
                <label className="form-label">
                  Name*
                   <OverlayTrigger
                      placement="right"
                      overlay={<Tooltip className="tone-name-tooltip cmn-summary-tooltip" id={input.uuid + "-tone"}>Style Name</Tooltip>}
                    >
                        <img src={images["info.svg"]} alt="" className="ms-2 cursor-pointer" />
                    </OverlayTrigger>
                </label>
                <Input
                  type="text"
                  placeholder="Crafting Professional Communication"
                  className="form-control"
                  value={input.name}
                  onChange={(e) => handleChangeInput("name", e.target.value)}
                  onFocus={() => setIsNameFocused(true)}
                  onBlur={() => setIsNameFocused(false)}
                />
                <span className="text-danger">{inputError.name}</span>
                <span className="chat-form-note position-static float-end">
                  {isNameFocused
                    ? <>{input.name.length}/{NAME_LENGTH}</>
                    : input.name.length
                  }
                  {" "} character{input.name.length > 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="col-md-12">
              <div className="form-group">
                <label className="form-label">
                  Description*
                  <OverlayTrigger
                    placement="right"
                      overlay={<Tooltip className="tone-desctiption-tooltip cmn-summary-tooltip" id={input.uuid + "-tone"}>Describe Your Writing Tone</Tooltip>}
                  >
                      <img src={images["info.svg"]} alt="" className="ms-2 cursor-pointer" />
                  </OverlayTrigger>
                </label>
                <textarea
                  placeholder="My writing style emphasizes professionalism and precision, with a focus on clear and concise communication. It's essential for creating formal documents and business correspondence."
                  className="form-control sum-prompt-textarea"
                  value={input.description}
                  onChange={(e) => handleChangeInput("description", e.target.value)}
                  rows={"3"}
                  style={{ height: "auto" }}
                  onFocus={() => setIsDescriptionFocused(true)}
                  onBlur={() => setIsDescriptionFocused(false)}
                />
                <span className="text-danger">{inputError.description}</span>
                <span className="chat-form-note position-static float-end">
                  {isDescriptionFocused
                    ? <>{input.description.length}/{DESCRIPTION_LENGTH}</>
                    : input.description.length
                  }
                  {" "} character{input.description.length > 1 ? "s" : ""}
                </span>
              </div>
            </div>
            <div className="col-12 col-sm-12 col-md-6">
              <div className="form-group">
                <label className="form-label">General Access</label>
                <Select
                  id="inputGroupSelect01"
                  name="entity"
                  options={generalAccessOptions}
                  placeholder="Private"
                  classNamePrefix="cmn-control"
                  menuPlacement="auto"
                  value={generalAccessOptions.find((opt) => opt.value === input.general_access)}
                  onChange={(e) => handleChangeInput("general_access", e.value)}
                />

                <span className="text-danger">{inputError.general_access}</span>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
        <div className="form-group mb-0">
                                <div className="form-check text-start footer-check">
                                    <Input
                                        onChange={(e) => handleChangeInput("is_default", !input.is_default)}
                                        className="form-check-input"
                                        type="checkbox"
                                        id="is_default"
                                        name="is_default"
                                        checked={input.is_default}
                                    />
                                    <label className="form-check-label" htmlFor="is_default">
                                        Set as default
                                    </label>
                                </div>
                            </div>
          <Button
            type="button"
            className="btn btn-primary-md"
            onClick={save}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ToneModal;
